import React, { useEffect, useMemo, useRef, useState } from 'react';
import useApiControl from '../../../common/useApiControl';
import { useSSETarget } from '../../../common/useSseControl';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ReviveRoomPointDialog from '../../dialogs/ReviveRoomPointDialog';
import ConfirmActionDialog from '../../dialogs/ConfirmActionDialog';
import BattleBalanceBar from '../BattleBalanceBar';
import BetSelectionUsersDialog from '../BetSelectionUsersDialog';
import BetChoiceCardSP from '../BetChoiceCardSP';
import { BET_ALREADY_MESSAGE, buildBetDisplayItems, normalizeBetChoices } from '../betLabels';
import { calculateCancelPreview } from '../betCancelPreview';

const parseAnswers = (rawAnswers) => {
  if (Array.isArray(rawAnswers)) {
    return normalizeBetChoices(rawAnswers);
  }

  if (typeof rawAnswers === 'string') {
    const trimmed = rawAnswers.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? normalizeBetChoices(parsed) : [];
    } catch (error) {
      console.error('Error parsing answers JSON', error);
      console.log(rawAnswers);
      return [];
    }
  }

  return [];
};

const normalizeBetPointsPayload = (payload, choices) => {
  const nextPoints = choices.map(() => 0);

  if (!Array.isArray(payload)) {
    return nextPoints;
  }

  payload.forEach((item, fallbackIndex) => {
    if (item && typeof item === 'object') {
      const selectionIndex = Number(item.selection);
      if (!Number.isInteger(selectionIndex) || selectionIndex < 0 || selectionIndex >= nextPoints.length) {
        return;
      }

      nextPoints[selectionIndex] = Number(item.total_point ?? item.totalPoint ?? item.point ?? 0);
      return;
    }

    if (fallbackIndex < nextPoints.length) {
      nextPoints[fallbackIndex] = Number(item || 0);
    }
  });

  return nextPoints;
};

const normalizeBetDetailsPayload = (payload, choices) => {
  const nextDetails = choices.map(() => null);

  if (!Array.isArray(payload)) {
    return nextDetails;
  }

  payload.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const selectionIndex = Number(item.selection);
    if (!Number.isInteger(selectionIndex) || selectionIndex < 0 || selectionIndex >= nextDetails.length) {
      return;
    }

    const detail = item.latest_bet || item;
    const point = Number(detail.point ?? item.total_point ?? item.totalPoint ?? 0);
    const rate = detail.rate ?? detail.option_rate ?? item.rate ?? item.option_rate ?? null;
    const expectedPayout = detail.expected_payout ?? item.expected_payout ?? (
      rate !== null && Number.isFinite(Number(rate)) ? Math.floor(Number(rate) * point) : null
    );

    nextDetails[selectionIndex] = {
      ...detail,
      selection: selectionIndex,
      point,
      rate,
      option_rate: detail.option_rate ?? rate,
      expected_payout: expectedPayout,
    };
  });

  return nextDetails;
};

const formatRate = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const rateValue = typeof value === 'object' ? (value.rate ?? value.optional_rate ?? value.base_rate) : value;
  if (rateValue === null || rateValue === undefined || rateValue === '') {
    return '-';
  }

  const numericValue = Number(rateValue);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(rateValue);
};

const BET_ENTRY_STATUS = {
  NOT_BET: 'notBet',
  EDITING: 'editing',
  PLACED: 'placed',
};

function BetTargetListSP({ betUsersBySelection = {}, items, onOpenBetUsers, titleRef }) {
  if (!items.length) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '14px',
        bgcolor: 'rgba(255,255,255,0.86)',
        border: '1px solid rgba(15, 23, 42, 0.10)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72)',
      }}
    >
      <Box
        ref={titleRef}
        sx={{ fontSize: '12px', fontWeight: 900, color: 'rgba(15, 23, 42, 0.72)', mb: 1 }}
      >
        今回のベット対象
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {items.map((item) => {
          const users = betUsersBySelection[item.index] || betUsersBySelection[String(item.index)] || [];
          const hasUsers = users.length > 0;
          return (
            <Box
              key={item.index}
              component={hasUsers ? 'button' : 'div'}
              type={hasUsers ? 'button' : undefined}
              onClick={hasUsers ? () => onOpenBetUsers?.(item) : undefined}
              sx={{
                width: '100%',
                appearance: 'none',
                textAlign: 'left',
                display: 'grid',
                gridTemplateColumns: '64px minmax(0, 1fr)',
                gap: 1,
                alignItems: 'start',
                fontSize: '13px',
                lineHeight: 1.45,
                p: hasUsers ? '6px 8px' : 0,
                borderRadius: hasUsers ? '10px' : 0,
                border: hasUsers ? `1px solid ${item.color.border}` : 'none',
                bgcolor: hasUsers ? 'rgba(255,255,255,0.64)' : 'transparent',
                cursor: hasUsers ? 'pointer' : 'default',
                transition: 'background-color 0.12s ease, transform 0.12s ease, border-color 0.12s ease',
                '&:active': hasUsers ? {
                  bgcolor: item.color.surface,
                  transform: 'scale(0.99)',
                } : undefined,
              }}
            >
              <Box sx={{ fontWeight: 900, color: item.color.primary, whiteSpace: 'nowrap' }}>
                {item.label}:
              </Box>
              <Box sx={{ minWidth: 0, color: item.color.primary, overflowWrap: 'anywhere' }}>
                {item.choice}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const RoundDitailSP = ({
  event,
  round,
  user,
  updateUser,
  displayPoint,
  highlightedBetSelections = [],
  betPopupUsersBySelection = {},
  setDisplayPoint,
  setPointChangeAmount,
  roundRateData: providedRoundRateData,
  summaryData,
  timePhase,
  betDisplayItems: providedBetDisplayItems,
  isRoomPointInsufficient,
  onBetTargetVisibilityChange,
}) => {
  const [viewPoints, setViewPoints] = useState([]);
  const [viewBetDetails, setViewBetDetails] = useState([]);
  const [viewRate, setViewRate] = useState([]);
  const roundStatus =
    event?.bet_close_mode === 'manual' && timePhase === 'ended'
      ? 'active'
      : (timePhase || 'waiting');
  const [highlightedIndexes] = useState([]);
  const [choices, setChoices] = useState(() => parseAnswers(round.answers));
  const [pendingBetPoints, setPendingBetPoints] = useState(() => parseAnswers(round.answers).map(() => 0));
  const [selectedBetValue, setSelectedBetValue] = useState(null);
  const [isBetSubmitting, setIsBetSubmitting] = useState(false);
  const [isBetCancelling, setIsBetCancelling] = useState(false);
  const [cancelConfirmDialogVisible, setCancelConfirmDialogVisible] = useState(false);
  const [reviveDialogVisible, setReviveDialogVisible] = useState(false);
  const [reviveResult, setReviveResult] = useState(null);
  const [reviveErrorMessage, setReviveErrorMessage] = useState('');
  const [betUsersDialogTarget, setBetUsersDialogTarget] = useState(null);
  const betTargetAnchorRef = useRef(null);
  const roundRateMessage = useSSETarget('roundRate');
  const ignoredRoundRateSeqRef = useRef(roundRateMessage?.__seq ?? null);
  const roundRateData = providedRoundRateData ?? (
    roundRateMessage?.__seq && roundRateMessage.__seq !== ignoredRoundRateSeqRef.current
      ? roundRateMessage.payload
      : null
  );

  const roundNumberRef = useRef(round.number);

  const normalizeRatePayload = (payload, currentRates) => {
    const nextRates = choices.map((_, index) => currentRates[index] ?? '-');
    const assignRate = (index, value) => {
      if (index < 0 || index >= nextRates.length || value === null || value === undefined || value === '') {
        return;
      }

      nextRates[index] = formatRate(value);
    };

    if (Array.isArray(payload)) {
      payload.forEach((value, index) => assignRate(index, value));
      return nextRates;
    }

    if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([key, value]) => assignRate(Number(key), value));
      return nextRates;
    }

    return null;
  };

  useEffect(() => {
    roundNumberRef.current = round.number;
  }, [round.number]);

  const values = useMemo(() => {
    const point = user.point || 0;
    const min = event.minimum_bet || 0;
    const max = event.max_bet || Infinity;

    if (point < min) {
      return [];
    }

    const lenPoint = point.toString().length;
    let base1 = 10 ** (lenPoint - 1);
    let base2 = 10 ** (lenPoint - 2);

    if (base1 > max / 10) {
      const maxDigits = Math.floor(Math.log10(max));
      base1 = 10 ** (maxDigits - 1);
      base2 = 10 ** (maxDigits - 2);
    }

    const betValues = [base1, base2].filter((value) => value >= min && value <= max);
    // 暫定対応: ベット額候補生成ロジックを見直すまで、候補0件の場合は最小ベット額を表示する。
    return betValues.length > 0 ? betValues : [min];
  }, [event.max_bet, event.minimum_bet, user.point]);

  const params1 = useMemo(
    () => ({
      event_id: event?.id,
      number: event?.round,
    }),
    [event],
  );

  const { data: data1, executeRequest: executeRequest1 } = useApiControl({
    action: '/get_my_bet',
    params: params1,
    method: 'POST',
  });

  const { executeRequest: executeRequest2 } = useApiControl({
    action: '/bet_action',
    method: 'POST',
  });

  const { executeRequest: executeCancelBet } = useApiControl({
    action: '/cancel_bet_action',
    method: 'POST',
  });

  const { loading: revivingPoint, executeRequest: executeRevivePoint } = useApiControl({
    action: '/revive_event_point',
    method: 'POST',
  });

  useEffect(() => {
    if (data1?.bets) {
      setViewPoints(normalizeBetPointsPayload(data1.bets, choices));
      setViewBetDetails(normalizeBetDetailsPayload(data1.bets, choices));
    }
  }, [choices, data1]);

  useEffect(() => {
    executeRequest1();
  }, []);

  useEffect(() => {
    ignoredRoundRateSeqRef.current = roundRateMessage?.__seq ?? null;
    setSelectedBetValue(null);
    setPendingBetPoints(choices.map(() => 0));
    setViewPoints(choices.map(() => 0));
    setViewBetDetails(choices.map(() => null));
    setViewRate(choices.map(() => '-'));
  }, [round.number]);

  useEffect(() => {
    const parsedChoices = parseAnswers(round.answers);
    if (round.answers) {
      console.log(round.answers);
    }
    setChoices(parsedChoices);
  }, [round.answers]);

  useEffect(() => {
    setSelectedBetValue(null);
    setPendingBetPoints(choices.map(() => 0));
    setViewPoints(choices.map(() => 0));
    setViewBetDetails(choices.map(() => null));
    setViewRate(choices.map(() => '-'));
  }, [choices]);

  useEffect(() => {
    if (!roundRateData) {
      return;
    }

    setViewRate((currentRates) => {
      const formatted = normalizeRatePayload(roundRateData, currentRates);
      if (formatted) {
        return formatted;
      }
      return currentRates;
    });
  }, [roundRateData, choices]);

  const displayRates = useMemo(() => {
    if (!roundRateData) {
      return viewRate;
    }

    return normalizeRatePayload(roundRateData, viewRate) || viewRate;
  }, [roundRateData, choices, viewRate]);

  const betDisplayItems = useMemo(
    () => providedBetDisplayItems || buildBetDisplayItems(choices),
    [choices, providedBetDisplayItems],
  );
  const betDisplayLabels = useMemo(
    () => betDisplayItems.map((item) => item.label),
    [betDisplayItems],
  );

  const confirmedBetIndex = useMemo(
    () => viewPoints.findIndex((point) => Number(point || 0) > 0),
    [viewPoints],
  );
  const hasConfirmedBet = confirmedBetIndex >= 0;
  const confirmedBetPoint = confirmedBetIndex >= 0 ? Number(viewPoints[confirmedBetIndex] || 0) : 0;
  const cancelPreview = useMemo(
    () => calculateCancelPreview(confirmedBetPoint),
    [confirmedBetPoint],
  );
  const cancelConfirmMessage = `ベットをキャンセルしますか？\n\nキャンセル時 ${cancelPreview.refundPoint}pt返却\n手数料 ${cancelPreview.feePoint}pt`;
  const pendingBetIndex = useMemo(
    () => pendingBetPoints.findIndex((point) => Number(point || 0) > 0),
    [pendingBetPoints],
  );
  const betEntryStatus = hasConfirmedBet
    ? BET_ENTRY_STATUS.PLACED
    : pendingBetIndex >= 0 ? BET_ENTRY_STATUS.EDITING : BET_ENTRY_STATUS.NOT_BET;

  const canBetOnSelection = (index) => !hasConfirmedBet && (pendingBetIndex < 0 || pendingBetIndex === index);

  const canSelectBetValue = (value) => {
    const betValue = parseInt(value, 10);
    if (
      hasConfirmedBet ||
      !Number.isFinite(betValue) ||
      betValue <= 0 ||
      !(roundStatus === 'fast' || roundStatus === 'active')
    ) {
      return false;
    }

    const nextSelectedBetValue = Number(selectedBetValue || 0) + betValue;
    return choices.some((_, index) => {
      if (!canBetOnSelection(index)) {
        return false;
      }

      return nextSelectedBetValue <= displayPoint && event.max_bet >= nextSelectedBetValue;
    });
  };

  const selectBetValue = (value) => {
    if (!canSelectBetValue(value)) {
      return;
    }

    setSelectedBetValue((currentValue) => Number(currentValue || 0) + parseInt(value, 10));
  };

  const canConfirmSelectedBet = (index) => {
    const betValue = Number(selectedBetValue || 0);
    if (hasConfirmedBet || betValue <= 0 || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return false;
    }

    return (
      canBetOnSelection(index) &&
      betValue <= displayPoint &&
      event.max_bet >= betValue
    );
  };

  const confirmBet = (index) => {
    const pendingPoint = Number(selectedBetValue ?? pendingBetPoints[index] ?? 0);
    if (
      pendingPoint <= 0 ||
      isBetSubmitting ||
      hasConfirmedBet ||
      !canBetOnSelection(index) ||
      pendingPoint > displayPoint ||
      event.max_bet < pendingPoint ||
      !(roundStatus === 'fast' || roundStatus === 'active')
    ) {
      return;
    }

    setIsBetSubmitting(true);
    executeRequest2({
      e: event.id,
      r: roundNumberRef.current,
      a: index,
      p: pendingPoint,
      t: new Date().toISOString(),
    })
      .then((response) => {
        const savedBet = response?.bet ?? {
          selection: index,
          point: pendingPoint,
          rate: displayRates[index],
          option_rate: displayRates[index],
          expected_payout: Number.isFinite(Number(displayRates[index]))
            ? Math.floor(Number(displayRates[index]) * pendingPoint)
            : null,
        };

        setPendingBetPoints((currentPendingPoints) => {
          const updatedPendingPoints = [...currentPendingPoints];
          updatedPendingPoints[index] = 0;
          return updatedPendingPoints;
        });
        setSelectedBetValue(null);
        setDisplayPoint((currentDisplayPoint) => currentDisplayPoint - pendingPoint);
        setViewPoints((currentViewPoints) => {
          const updatedViewPoints = [...currentViewPoints];
          updatedViewPoints[index] = Number(updatedViewPoints[index] || 0) + pendingPoint;
          return updatedViewPoints;
        });
        setViewBetDetails((currentDetails) => {
          const updatedDetails = [...currentDetails];
          updatedDetails[index] = savedBet;
          return updatedDetails;
        });
        setIsBetSubmitting(false);
      })
      .catch((error) => {
        console.error(error);
        if (error?.response?.status === 409) {
          alert(error.response?.data?.message || BET_ALREADY_MESSAGE);
          executeRequest1().catch((fetchError) => {
            console.error(fetchError);
          });
        }
        setIsBetSubmitting(false);
        updateUser();
      });
  };

  const cancelSelectedBet = () => {
    setSelectedBetValue(null);
    setPendingBetPoints((currentPendingPoints) => currentPendingPoints.map(() => 0));
  };

  const openCancelConfirmDialog = () => {
    if (!hasConfirmedBet || isBetCancelling || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return;
    }

    setCancelConfirmDialogVisible(true);
  };

  const cancelConfirmedBet = () => {
    if (!hasConfirmedBet || isBetCancelling || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return;
    }

    setIsBetCancelling(true);
    executeCancelBet({
      e: event.id,
      r: roundNumberRef.current,
      t: new Date().toISOString(),
    })
      .then((response) => {
        const refundPoint = Number(response?.refund_point || 0);
        setSelectedBetValue(null);
        setPendingBetPoints(choices.map(() => 0));
        setViewPoints(choices.map(() => 0));
        setViewBetDetails(choices.map(() => null));
        setDisplayPoint((currentDisplayPoint) => (
          response?.room_point !== null && response?.room_point !== undefined
            ? Number(response.room_point)
            : currentDisplayPoint + refundPoint
        ));
        if (setPointChangeAmount && response?.point_change) {
          setPointChangeAmount(response.point_change);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(error?.response?.data?.message || 'ベットをキャンセルできませんでした。');
      })
      .finally(() => {
        setIsBetCancelling(false);
        setCancelConfirmDialogVisible(false);
        updateUser();
      });
  };

  const reviveRequiredPoint = Math.max((event?.minimum_bet || 0) - (user?.point || 0), 0);
  const roomPointInsufficient = isRoomPointInsufficient ?? reviveRequiredPoint > 0;
  const reviveCount = Number(user?.revive_count || 0);
  const remainingRevives = Math.max(3 - reviveCount, 0);
  const alreadyRevivedThisRound =
    Number(user?.last_revived_round || 0) === Number(event?.round || 0);
  const reviveUnavailableMessage =
    reviveRequiredPoint <= 0
      ? ''
      : event?.completion_status && event.completion_status !== 'active'
        ? '終了したルームでは復活できません。'
        : (user?.ticket || 0) < reviveRequiredPoint
          ? '参加チケットが不足しているため復活できません。'
          : remainingRevives <= 0
              ? 'このイベントでの復活回数上限に達しています。'
              : alreadyRevivedThisRound
                ? 'このラウンドではすでに復活済みです。'
                : !(roundStatus === 'fast' || roundStatus === 'active')
                  ? 'ベット可能時間中のみ復活できます。'
                  : '';
  const canReviveByRule = reviveRequiredPoint > 0 && !reviveUnavailableMessage;
  const showReviveNotice =
    round.type === 0 && roomPointInsufficient && reviveRequiredPoint > 0;

  const revivePreview = {
    moved_points: reviveRequiredPoint,
    before_ticket: Number(user?.ticket || 0),
    ticket: Math.max(Number(user?.ticket || 0) - reviveRequiredPoint, 0),
    before_room_point: Number(user?.point || 0),
    room_point: Number(user?.point || 0) + reviveRequiredPoint,
    revive_count: reviveCount,
    remaining_revives: remainingRevives,
  };

  const openReviveDialog = () => {
    setReviveResult(null);
    setReviveErrorMessage(reviveUnavailableMessage);
    setReviveDialogVisible(true);
  };

  const handleReviveConfirm = () => {
    executeRevivePoint({ event_id: event.id })
      .then((response) => {
        setReviveResult(response);
        setReviveErrorMessage('');
        updateUser();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          '復活ポイントの移送に失敗しました。';
        setReviveErrorMessage(message);
      });
  };

  const infoMsgSx = {
    mt: 0.75,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.95em',
    lineHeight: 1.4,
    bgcolor: 'rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.10)',
    color: 'text.secondary',
  };

  const warnMsgSx = {
    ...infoMsgSx,
    bgcolor: 'rgba(255, 193, 7, 0.12)',
    border: '1px solid rgba(255, 193, 7, 0.35)',
    color: 'rgba(120, 84, 0, 0.95)',
    fontWeight: 700,
  };

  useEffect(() => {
    if (!onBetTargetVisibilityChange || !betTargetAnchorRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        onBetTargetVisibilityChange(entry.isIntersecting && entry.intersectionRatio >= 1);
      },
      {
        threshold: [0, 1],
        rootMargin: '-8px 0px 0px 0px',
      },
    );

    observer.observe(betTargetAnchorRef.current);
    return () => observer.disconnect();
  }, [onBetTargetVisibilityChange, betDisplayItems.length, round.number]);

  const sharedBetControlSx = {
    p: 1.25,
    borderRadius: '12px',
    bgcolor: 'rgba(255,255,255,0.78)',
    border: '1px solid rgba(0,0,0,0.12)',
  };

  const reviveNoticeSx = {
    mt: 1,
    p: 1.5,
    borderRadius: '12px',
    background:
      'linear-gradient(135deg, rgba(255, 183, 77, 0.18) 0%, rgba(255, 224, 178, 0.36) 100%)',
    border: '1px solid rgba(245, 124, 0, 0.28)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
  };
  const selectedBetUsers = betUsersDialogTarget
    ? (betPopupUsersBySelection[betUsersDialogTarget.index] || betPopupUsersBySelection[String(betUsersDialogTarget.index)] || [])
    : [];

  return (
    <>
      <div
        style={{
          padding: '20px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          fontSize: '14px',
          color: '#444',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        {roomPointInsufficient && (
          <Box sx={warnMsgSx}>ルームチップ不足です。</Box>
        )}

        {(choices.length === 0 || roundStatus === 'waiting') && (
          <Box sx={{ ...infoMsgSx, bgcolor: 'rgba(0,0,0,0.03)' }}>
            ゲーム開始までお待ちください
          </Box>
        )}

        {roundStatus === 'ended' && (
          <Box sx={{ ...infoMsgSx, bgcolor: 'rgba(0,0,0,0.03)' }}>
            ラウンドは終了しました
          </Box>
        )}

        {round.type === 0 && choices.length >= 2 && (
          <BetTargetListSP
            betUsersBySelection={betPopupUsersBySelection}
            items={betDisplayItems}
            onOpenBetUsers={setBetUsersDialogTarget}
            titleRef={betTargetAnchorRef}
          />
        )}

        {round.type === 0 && choices.length >= 2 && (
          <BattleBalanceBar
            roundKey={round.id ?? round.number}
            choices={choices}
            displayItems={betDisplayItems}
            compact
            displayLabels={betDisplayLabels}
            highlightedSelections={highlightedBetSelections}
            summaryData={summaryData}
            rateData={displayRates}
          />
        )}

        {showReviveNotice && (
          <Box sx={reviveNoticeSx}>
            <Box sx={{ fontSize: '13px', fontWeight: 800, color: '#9a3412', mb: 0.75 }}>
              ルームチップ不足でベットできません
            </Box>
            <Box sx={{ fontSize: '12px', color: 'rgba(120, 84, 0, 0.92)', lineHeight: 1.6, mb: 1.25 }}>
              参加チケットから {reviveRequiredPoint} 枚を移して、最小ベット可能状態に戻せます。
              <br />
              1ラウンド1回 / 1イベント最大3回 / 不足分のみ移送されます。
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ fontSize: '12px', color: 'rgba(120, 84, 0, 0.88)', lineHeight: 1.6 }}>
                参加チケット {user?.ticket || 0}枚 → {revivePreview.ticket}枚
                <br />
                Room {user?.point || 0} pt → {revivePreview.room_point} pt
              </Box>
              <Button
                variant="contained"
                onClick={openReviveDialog}
                disabled={!canReviveByRule}
                sx={{
                  minWidth: '160px',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '999px',
                  boxShadow: 'none',
                  background: 'linear-gradient(135deg, #ef6c00 0%, #fb8c00 100%)',
                  '&:hover': {
                    boxShadow: '0 6px 14px rgba(239, 108, 0, 0.24)',
                    background: 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)',
                  },
                }}
              >
                復活ポイントを確認
              </Button>
            </Box>
            {reviveUnavailableMessage && (
              <Box sx={{ mt: 1, fontSize: '12px', color: '#9a3412', fontWeight: 700 }}>
                {reviveUnavailableMessage}
              </Box>
            )}
          </Box>
        )}

        {!roomPointInsufficient && round.type === 0 && choices.length >= 2 && (
          <Box sx={sharedBetControlSx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.75,
              }}
            >
              <Box sx={{ fontSize: '12px', fontWeight: 900, color: 'rgba(0,0,0,0.62)' }}>
                ベット予定
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.35,
                  minWidth: '76px',
                  borderRadius: '8px',
                  bgcolor: selectedBetValue ? '#fff7ed' : '#fff',
                  border: `1px solid ${selectedBetValue ? 'rgba(245, 124, 0, 0.46)' : 'rgba(0,0,0,0.12)'}`,
                  color: selectedBetValue ? '#9a3412' : 'rgba(0,0,0,0.54)',
                  fontSize: '13px',
                  fontWeight: 900,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedBetValue ? `${selectedBetValue} pt` : '未選択'}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {values.map((value) => (
                <Button
                  key={value}
                  variant={selectedBetValue === value ? 'contained' : 'outlined'}
                  disabled={!canSelectBetValue(value) || isBetSubmitting}
                  onClick={() => selectBetValue(value)}
                  sx={{
                    minWidth: '84px',
                    height: '36px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: 800,
                    boxShadow: 'none',
                    textTransform: 'none',
                  }}
                >
                  {value} pt
                </Button>
              ))}
              <Button
                variant="outlined"
                disabled={!selectedBetValue || isBetSubmitting}
                onClick={cancelSelectedBet}
                sx={{
                  minWidth: '96px',
                  height: '36px',
                  borderRadius: '999px',
                  fontSize: '13px',
                  fontWeight: 800,
                  textTransform: 'none',
                }}
              >
                キャンセル
              </Button>
            </Box>
          </Box>
        )}

        {!roomPointInsufficient && round.type === 0 && betDisplayItems.map((item) => (
          <BetChoiceCardSP
            key={item.index}
            displayLabel={item.label}
            colorSet={item.color}
            index={item.index}
            rate={displayRates[item.index] ?? '-'}
            betPoint={viewPoints[item.index]}
            betDetail={viewBetDetails[item.index]}
            pendingPoint={canConfirmSelectedBet(item.index) ? selectedBetValue : pendingBetPoints[item.index]}
            canBetTime={roundStatus === 'fast' || roundStatus === 'active'}
            isHighlighted={highlightedIndexes.includes(item.index) || highlightedBetSelections.includes(item.index)}
            disabledBySideLock={!canBetOnSelection(item.index)}
            sideLockMessage={BET_ALREADY_MESSAGE}
            betEntryStatus={betEntryStatus}
            onConfirmBet={confirmBet}
            isBetSubmitting={isBetSubmitting}
            onCancelConfirmedBet={openCancelConfirmDialog}
            isBetCancelling={isBetCancelling}
            canCancelConfirmedBet={hasConfirmedBet && confirmedBetIndex === item.index && (roundStatus === 'fast' || roundStatus === 'active')}
          />
        ))}


      </div>

      <ReviveRoomPointDialog
        isVisible={reviveDialogVisible}
        onClose={() => setReviveDialogVisible(false)}
        onConfirm={handleReviveConfirm}
        loading={revivingPoint}
        canRevive={canReviveByRule}
        preview={revivePreview}
        result={reviveResult}
        errorMessage={reviveErrorMessage}
      />
      <ConfirmActionDialog
        isVisible={cancelConfirmDialogVisible}
        onClose={() => setCancelConfirmDialogVisible(false)}
        onConfirm={cancelConfirmedBet}
        message={cancelConfirmMessage}
        confirmLabel="キャンセルする"
        cancelLabel="戻る"
        loading={isBetCancelling}
      />
      <BetSelectionUsersDialog
        colorSet={betUsersDialogTarget?.color}
        displayLabel={betUsersDialogTarget?.label || betUsersDialogTarget?.shortLabel || '選択肢'}
        isVisible={Boolean(betUsersDialogTarget)}
        onClose={() => setBetUsersDialogTarget(null)}
        responsiveMode="mobile"
        users={selectedBetUsers}
      />
    </>
  );
};

export default RoundDitailSP;
